import { useState } from "react";

import { DatePicker } from "@/components/ui/date-picker";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useCustomers } from "@/hooks/useCustomers";
import { ReportCard } from "./ReportCard";

/** Controlled From/To pair shared by the period-scoped report cards. */
function PeriodFields(props: {
  start: string;
  end: string;
  onStart: (v: string) => void;
  onEnd: (v: string) => void;
}) {
  return (
    <div className="grid grid-cols-2 gap-3">
      <div className="flex flex-col gap-2">
        <Label>From</Label>
        <DatePicker value={props.start} onChange={props.onStart} />
      </div>
      <div className="flex flex-col gap-2">
        <Label>To</Label>
        <DatePicker value={props.end} onChange={props.onEnd} />
      </div>
    </div>
  );
}

const EXPIRY_WINDOWS = ["30", "60", "90", "180"] as const;

export function MaxwellReportsPage() {
  const [pipelineStart, setPipelineStart] = useState("");
  const [pipelineEnd, setPipelineEnd] = useState("");
  const [winLossStart, setWinLossStart] = useState("");
  const [winLossEnd, setWinLossEnd] = useState("");
  const [commissionStart, setCommissionStart] = useState("");
  const [commissionEnd, setCommissionEnd] = useState("");
  const [expiryWindow, setExpiryWindow] = useState("90");
  const [qbrCustomerId, setQbrCustomerId] = useState("");
  const [qbrStart, setQbrStart] = useState("");
  const [qbrEnd, setQbrEnd] = useState("");

  // The QBR customer picker is a bounded auto-fetch picker (first page of
  // customers by name); the server enforces customer_id regardless.
  const { data: customers } = useCustomers({ sort: "name", per_page: 100 });

  return (
    <div className="flex flex-col gap-5">
      <p className="text-sm text-muted-foreground">
        Branded PDF reports over your live book — download or ask Maxwell for one.
      </p>

      <div className="grid gap-4 md:grid-cols-2">
        <ReportCard
          kind="pipeline_review"
          title="Pipeline Review"
          description="Open-pipeline snapshot: stage funnel, open gross margin, and top open deals."
          controls={
            <PeriodFields
              start={pipelineStart}
              end={pipelineEnd}
              onStart={setPipelineStart}
              onEnd={setPipelineEnd}
            />
          }
          buildInput={() => ({
            period_start: pipelineStart || undefined,
            period_end: pipelineEnd || undefined,
          })}
        />

        <ReportCard
          kind="win_loss"
          title="Win / Loss Analysis"
          description="Closed outcomes for the period: win rate, loss reasons, and recent decisions."
          controls={
            <PeriodFields
              start={winLossStart}
              end={winLossEnd}
              onStart={setWinLossStart}
              onEnd={setWinLossEnd}
            />
          }
          buildInput={() => ({
            period_start: winLossStart || undefined,
            period_end: winLossEnd || undefined,
          })}
        />

        <ReportCard
          kind="commission_statement"
          title="Commission Statement"
          description="Projected payouts for the period's closed-won deals, by payee, level, and type."
          controls={
            <PeriodFields
              start={commissionStart}
              end={commissionEnd}
              onStart={setCommissionStart}
              onEnd={setCommissionEnd}
            />
          }
          buildInput={() => ({
            period_start: commissionStart || undefined,
            period_end: commissionEnd || undefined,
          })}
        />

        <ReportCard
          kind="portfolio_expiry"
          title="Portfolio Expiry"
          description="Contracts expiring inside the window — the renewal radar on paper."
          controls={
            <div className="flex flex-col gap-2">
              <Label>Window</Label>
              <Select value={expiryWindow} onValueChange={setExpiryWindow}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {EXPIRY_WINDOWS.map((w) => (
                    <SelectItem key={w} value={w}>
                      {w} days
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          }
          buildInput={() => ({ within_days: Number(expiryWindow) })}
        />

        <ReportCard
          kind="customer_qbr"
          title="Customer QBR"
          description="One client's quarterly business review: relationship KPIs, deals, and contracts."
          controls={
            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-2">
                <Label>Customer</Label>
                <Select value={qbrCustomerId} onValueChange={setQbrCustomerId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choose a customer…" />
                  </SelectTrigger>
                  <SelectContent>
                    {(customers?.items ?? []).map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <PeriodFields
                start={qbrStart}
                end={qbrEnd}
                onStart={setQbrStart}
                onEnd={setQbrEnd}
              />
            </div>
          }
          buildInput={() => ({
            customer_id: qbrCustomerId,
            period_start: qbrStart || undefined,
            period_end: qbrEnd || undefined,
          })}
          disabled={!qbrCustomerId}
        />
      </div>
    </div>
  );
}

export default MaxwellReportsPage;
